import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import { ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import { Table, Button, Typography, DatePicker, Row, Col, Card, Statistic, Empty } from 'antd';
import BaseSelect from '@/components/Elements/BaseSelect';
import moment from 'moment';
import { cleanObject } from '@/utils/helpers';
import { getAssistantsSupportCases, assistantsSupportCaseExportExcel, assistantsSupportCaseExportAllExcel } from '@/redux/actions/statisticsActions';
import { checkPermission } from '@/utils/permission';
import { NotificationManager } from 'react-notifications';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/assistants-support-cases', breadcrumbName: 'Thống kê ca hỗ trợ của phụ tá' },
];

export default function AssistantsSupportCases() {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const authUser = useSelector((state) => state.auth.authUser);
    const users = useSelector((state) => state.config.users);

    const { summary, detail } = useSelector(
        (state) => state.statisticsReducer.assistants_support_cases
    );

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingExportPersonal, setIsLoadingExportPersonal] = useState(false);
    const [isLoadingExportAll, setIsLoadingExportAll] = useState(false);

    const showStaffSelect = authUser?.role_code === 'ADMIN' || authUser?.role_code === 'KETOAN';
    const allowCustomerDetail = checkPermission('customer', 'detail');

    const query = qs.parse(location.search.slice(1));

    const allowExport = checkPermission('statistics', 'assistants_support_case_export');
    const allowExportAll = checkPermission('statistics', 'assistants_support_case_export_all');
    const canExportAll = showStaffSelect && allowExportAll;

    const formatDate = (d) =>
        moment.isMoment(d) && d.isValid() ? d.format('YYYY-MM-DD') : null;

    const isQueryReady = () => {
        const q = qs.parse(location.search.slice(1));

        if (!q.start_date || !q.end_date) return false;

        if (showStaffSelect) {
            if (!q.user_id) return false;   // admin phải chọn user_id
        }

        if (!showStaffSelect) {
            if (!authUser?.id) return false;
        }

        return true;
    };

    useEffect(() => {
        let q = qs.parse(location.search.slice(1));
        let updated = false;
        const newQ = { ...q };

        // 1) Default date
        if (!newQ.start_date || !newQ.end_date) {
            newQ.start_date = moment().startOf('month').format('YYYY-MM-DD');
            newQ.end_date = moment().endOf('month').format('YYYY-MM-DD');
            updated = true;
        }

        // 2) Auto gán user_id mặc định cho CẢ user thường và admin
        //    (admin mặc định xem báo cáo của chính họ, sau đó có thể đổi staff)
        const uid = authUser?.id ? String(authUser.id) : '';
        if (!newQ.user_id && uid) {
            newQ.user_id = uid;
            updated = true;
        }

        if (updated) {
            history.replace({
                pathname: location.pathname,
                search: qs.stringify(cleanObject(newQ)),
            });
        }
    }, [location.pathname, location.search, authUser?.id, showStaffSelect]);


    const loadData = async () => {
        const q = qs.parse(location.search.slice(1));

        const user_id = showStaffSelect ? q.user_id : authUser?.id;
        if (!user_id) return;

        setIsLoading(true);

        const params = {
            assistant_id: user_id,
            from_date: q.start_date,
            to_date: q.end_date,
            page: Number(q.page) || 1,
            per_page: Number(q.per_page) || 30,
        };

        try {
            await dispatch(getAssistantsSupportCases(params));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isQueryReady()) return;
        loadData();
    }, [location.search]);

    const onChangeFilter = (name, value) => {
        let q = { ...query };

        if (name === 'user_id') {
            value ? (q.user_id = value) : delete q.user_id;
        }

        if (name === 'date') {
            const [start, end] = value || [];
            q.start_date = formatDate(start);
            q.end_date = formatDate(end);
        }

        delete q.page;
        q = cleanObject(q);

        history.push({
            pathname: location.pathname,
            search: qs.stringify(q),
        });
    };

    const startExcelExport = () => {
        setIsLoadingExportPersonal(true);

        const q = qs.parse(location.search.slice(1));

        const user_id = showStaffSelect ? q.user_id : authUser?.id;
        if (!user_id) {
            setIsLoadingExportPersonal(false);
            return;
        }

        const params = {
            assistant_id: user_id,
            from_date: q.start_date,
            to_date: q.end_date
        };

        dispatch(assistantsSupportCaseExportExcel(params)).catch(() => {
            NotificationManager.error(
                "Có lỗi xảy ra trong quá trình gửi yêu cầu xuất file. Vui lòng thử lại sau.",
                "Lỗi",
                9000
            );
        }).finally(() => {
            setIsLoadingExportPersonal(false);
        });

        NotificationManager.success(
            `Quá trình xuất file đang được thực hiện. 
        File kết quả sẽ được gửi vào email ${authUser.user_email} trong ít phút.`,
            "Thông báo",
            9000
        );
    };

    const startExcelExportAll = () => {
        setIsLoadingExportAll(true);

        const q = qs.parse(location.search.slice(1));

        const params = {
            from_date: q.start_date,
            to_date: q.end_date
        };

        dispatch(assistantsSupportCaseExportAllExcel(params)).catch(() => {
            NotificationManager.error(
                "Có lỗi xảy ra trong quá trình gửi yêu cầu xuất file. Vui lòng thử lại sau.",
                "Lỗi",
                9000
            );
        }).finally(() => {
            setIsLoadingExportAll(false);
        });

        NotificationManager.success(
            `Quá trình xuất file toàn bộ phụ tá đang được thực hiện.
        File kết quả sẽ được gửi vào email ${authUser.user_email} trong ít phút.`,
            "Thông báo",
            9000
        );
    };

    const columns = [
        {
            title: 'Tên khách hàng',
            dataIndex: 'customer_name',
            render: (value, record) => (
                <strong>
                    {allowCustomerDetail ? (
                        <a
                            href={`/customer/${record?.customer_id}?tabs=treatment_sessions`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="color-primary item-action-btn"
                        >
                            {value}
                        </a>
                    ) : (
                        value
                    )}
                </strong>
            ),
        },
        {
            title: 'Thủ thuật',
            dataIndex: 'procedures',
            width: 300
        },
        {
            title: 'Điểm thủ thuật',
            dataIndex: 'total_procedure_points',
            align: 'center',
            width: 400
        },
        {
            title: 'Ngày phụ ca',
            dataIndex: 'ngay',
            align: 'center',
            render: (value) => moment(value).format('DD/MM/YYYY'),
        },
    ];

    return (
        <div>
            <PageTitle routes={routes} title="Thống kê ca hỗ trợ của phụ tá" />

            <div className="mt-2">
                <Title level={3}>Thống kê ca hỗ trợ của phụ tá</Title>

                <div className="mt-2 mb-2">
                    <Row gutter={16}>
                        <Col xs={24} md={24}>
                            {/* ADMIN chọn nhân viên */}
                            {showStaffSelect && (
                                <BaseSelect
                                    options={users}
                                    optionValue="user_id"
                                    optionLabel="display_name"
                                    placeholder="Chọn nhân viên"
                                    className="table-button"
                                    showSearch
                                    style={{ width: 300 }}
                                    value={query.user_id ? Number(query.user_id) : undefined}
                                    onChange={(value) => onChangeFilter('user_id', value)}
                                />
                            )}
                            <RangePicker
                                format="DD/MM/YYYY"
                                allowClear={false}
                                className="table-button"
                                value={[
                                    query.start_date ? moment(query.start_date) : null,
                                    query.end_date ? moment(query.end_date) : null,
                                ]}
                                onChange={(value) => onChangeFilter('date', value)}
                            />
                            {allowExport && (
                                <Button
                                    className="table-button"
                                    loading={isLoadingExportPersonal}
                                    type="default"
                                    onClick={startExcelExport}
                                    icon={<ExportOutlined />}
                                >
                                    Export cá nhân
                                </Button>
                            )}

                            {canExportAll && (
                                <Button
                                    className="table-button"
                                    loading={isLoadingExportAll}
                                    type="default"
                                    onClick={startExcelExportAll}
                                    icon={<ExportOutlined />}
                                >
                                    Export toàn bộ phụ tá
                                </Button>
                            )}
                            <Button
                                icon={<ReloadOutlined />}
                                className="table-button"
                                loading={isLoading}
                                onClick={loadData}
                            >
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                </div>

                {/* Chưa sẵn sàng dữ liệu */}
                {!summary && !isLoading && (
                    <div style={{ marginTop: 32, textAlign: 'center', opacity: 0.6 }}>
                        <Empty description="Vui lòng chọn nhân viên để xem báo cáo" />
                    </div>
                )}

                {/* HIỂN THỊ KẾT QUẢ */}
                {summary && (
                    <>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col xs={24} md={8}>
                                <Card>
                                    <Statistic
                                        title="Phụ tá"
                                        value={summary.assistant_name}
                                        valueStyle={{ fontSize: 20 }}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} md={8}>
                                <Card>
                                    <Statistic
                                        title="Tổng số ca phụ"
                                        value={summary.total_support_cases}
                                        valueStyle={{ fontSize: 20 }}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} md={8}>
                                <Card>
                                    <Statistic
                                        title="Tổng điểm thủ thuật"
                                        value={summary.total_procedure_points}
                                        valueStyle={{ fontSize: 20 }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Table
                            size="small"
                            rowKey={(_, i) => i}
                            columns={columns}
                            dataSource={detail}
                            loading={isLoading}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
